/**
 * Helpdesk Zustand store — drawer state + form state persistence.
 * A drawer bezárás-nyitás között megmarad a form állapota.
 */
import { create } from 'zustand';

export type TicketType = 'bug' | 'feature' | 'question';

export interface HelpdeskFormState {
  type: TicketType;
  reporter: string;
  module: string;
  screen: string;
  // Bug guided fields
  guidedAction: string;
  guidedResult: string;
  guidedExpected: string;
  // Free text
  description: string;
  // Bug/Feature specific
  severity: string;
  priority: string;
  reason: string;
  // Mode
  guidedMode: boolean;
  // Screenshots (base64)
  screenshots: string[];
  // Auto-capture csatolás
  attachConsoleErrors: boolean;
  attachFailedApis: boolean;
}

interface HelpdeskState {
  isOpen: boolean;
  activeView: 'form' | 'tickets' | 'success';
  form: HelpdeskFormState;
  lastTicketId: string;
  lastSheetUrl: string;
  // Actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  setActiveView: (view: 'form' | 'tickets' | 'success') => void;
  updateForm: (partial: Partial<HelpdeskFormState>) => void;
  addScreenshot: (base64: string) => void;
  removeScreenshot: (index: number) => void;
  setSuccess: (ticketId: string, sheetUrl: string) => void;
  resetForm: () => void;
}

const defaultForm: HelpdeskFormState = {
  type: 'bug',
  reporter: '',
  module: '',
  screen: '',
  guidedAction: '',
  guidedResult: '',
  guidedExpected: '',
  description: '',
  severity: '',
  priority: '',
  reason: '',
  guidedMode: true,
  screenshots: [],
  attachConsoleErrors: true,
  attachFailedApis: true,
};

export const useHelpdeskStore = create<HelpdeskState>(set => ({
  isOpen: false,
  activeView: 'form',
  form: { ...defaultForm },
  lastTicketId: '',
  lastSheetUrl: '',

  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set(s => ({ isOpen: !s.isOpen })),

  setActiveView: view => set({ activeView: view }),

  updateForm: partial => set(s => ({ form: { ...s.form, ...partial } })),

  addScreenshot: base64 =>
    set(s => {
      if (s.form.screenshots.length >= 3) return s;
      return { form: { ...s.form, screenshots: [...s.form.screenshots, base64] } };
    }),

  removeScreenshot: index =>
    set(s => ({
      form: {
        ...s.form,
        screenshots: s.form.screenshots.filter((_, i) => i !== index),
      },
    })),

  setSuccess: (ticketId, sheetUrl) =>
    set({ lastTicketId: ticketId, lastSheetUrl: sheetUrl, activeView: 'success' }),

  resetForm: () =>
    set(s => ({
      form: { ...defaultForm, reporter: s.form.reporter },
      activeView: 'form',
    })),
}));
